/**
 * 後台 — 互動分析(#16 意圖 / 功能使用)
 *
 * 關鍵字 / 功能使用排行(最多人用)+ 各類型計數 + 互動明細(可按類型篩選)。
 * 資料來源:interactions collection(每次會員互動 log 一筆)。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminListInteractions,
  adminInteractionStats,
  type InteractionRow,
  type InteractionStats,
} from "../../api/admin";
import AdminHeader from "../../components/AdminHeader";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";

const TYPE_LABELS: Record<string, string> = {
  message: "訊息",
  postback: "按鈕點擊",
  view_product: "看商品",
  ask_faq: "問 FAQ",
  view_trace: "查溯源",
  send_gift: "送禮",
  order: "下單",
  open_wallet: "開存摺",
  attend_event: "活動",
  onboarding_complete: "加入會員",
  profile_complete: "補齊姓名手機",
};
function typeLabel(t: string) {
  return TYPE_LABELS[t] || t || "—";
}

export default function InteractionAnalytics() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const showToast = useUIStore((s) => s.showToast);

  const [stats, setStats] = useState<InteractionStats | null>(null);
  const [rows, setRows] = useState<InteractionRow[]>([]);
  const [filterType, setFilterType] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const [s, r] = await Promise.all([
          adminInteractionStats(),
          adminListInteractions({ limit: 200 }),
        ]);
        setStats(s);
        setRows(r);
      } catch {
        showToast("載入失敗", "error");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  async function applyFilter(t: string) {
    setFilterType(t);
    try {
      setRows(await adminListInteractions({ type: t || undefined, limit: 200 }));
    } catch {
      showToast("載入失敗", "error");
    }
  }

  const maxCount = stats?.ranking[0]?.count || 1;

  return (
    <div className="min-h-screen bg-cream">
      <AdminHeader active="interactions" />
      <div className="p-6 max-w-4xl">
        <h1 className="text-xl font-bold text-ink mb-5">互動分析</h1>

        {loading ? (
          <div className="card py-10 text-center text-muted">載入中…</div>
        ) : (
          <>
            {/* 各類型計數 */}
            {stats && (
              <div className="card mb-5">
                <div className="text-sm font-bold text-ink mb-3">
                  各類型互動<span className="text-muted font-normal text-xs ml-2">取樣最近 {stats.total_sampled} 筆</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {stats.by_type.map((b) => (
                    <span key={b.type} className="text-xs bg-cream border border-line rounded-full px-3 py-1">
                      {typeLabel(b.type)} <span className="font-bold text-primary">{b.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 關鍵字 / 功能使用排行 */}
            {stats && stats.ranking.length > 0 && (
              <div className="card mb-5">
                <div className="text-sm font-bold text-ink mb-3">關鍵字 / 功能使用排行(最多人用)</div>
                <div className="space-y-2.5">
                  {stats.ranking.map((r, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="text-xs text-muted w-5 text-right pt-0.5">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm text-ink break-all">{r.content}</span>
                          <span className="text-sm font-bold text-primary shrink-0">{r.count}</span>
                        </div>
                        <div className="h-1.5 bg-cream rounded mt-1 overflow-hidden">
                          <div className="h-1.5 bg-primary rounded" style={{ width: `${(r.count / maxCount) * 100}%` }} />
                        </div>
                        <span className="text-xxs text-muted">{typeLabel(r.type)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 互動明細 + 篩選 */}
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-bold text-ink">互動明細</div>
                <select
                  className="input max-w-40 text-sm"
                  value={filterType}
                  onChange={(e) => applyFilter(e.target.value)}
                >
                  <option value="">全部類型</option>
                  {stats?.by_type.map((b) => (
                    <option key={b.type} value={b.type}>{typeLabel(b.type)}</option>
                  ))}
                </select>
              </div>
              {rows.length === 0 ? (
                <div className="py-8 text-center text-muted text-sm">無互動記錄</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted text-xs border-b border-line">
                      <th className="py-2">類型</th>
                      <th className="py-2">內容</th>
                      <th className="py-2">會員</th>
                      <th className="py-2 text-right">時間</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-b border-line/50">
                        <td className="py-2">
                          <span className="text-xs bg-cream rounded px-2 py-0.5 whitespace-nowrap">{typeLabel(r.type)}</span>
                        </td>
                        <td className="py-2 text-ink break-all">{r.content || "—"}</td>
                        <td className="py-2 text-muted text-xs font-mono whitespace-nowrap">{r.member_uid.slice(0, 8)}…</td>
                        <td className="py-2 text-right text-muted text-xs whitespace-nowrap">
                          {r.timestamp
                            ? new Date(r.timestamp).toLocaleString("zh-TW", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="text-xs text-muted mt-3 text-right">共 {rows.length} 筆</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
